import { useState, useEffect, useCallback, useRef } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * useNudgeSystem - Hook for managing Gentle Nudge notifications
 * 
 * Platform Support Notes:
 * - Chrome/Edge (Desktop & Android): Full support
 * - Firefox (Desktop): Full support
 * - Safari 16+ (Desktop): Supported
 * - Safari iOS 16.4+ (PWA only): Supported when installed to home screen
 * - Safari iOS (browser): NOT supported
 * - All other browsers: Varies
 */
export const useNudgeSystem = (userId) => {
  const [permissionStatus, setPermissionStatus] = useState("default");
  const [isSupported, setIsSupported] = useState(false);
  const [isIOSSafariNoPWA, setIsIOSSafariNoPWA] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Check notification support on mount
  useEffect(() => {
    const checkSupport = () => {
      // Detect iOS — including iPhone/iPad running Safari with "Request
      // Desktop Website" (UA looks like macOS, but touch support gives it
      // away). Some iOS 17+ builds also expose non-standard platform strings.
      const ua = navigator.userAgent || "";
      const platform = navigator.platform || "";
      const iOSByUA = /iPad|iPhone|iPod/.test(ua);
      const iOSAsDesktop = platform === 'MacIntel' && navigator.maxTouchPoints > 1;
      const iOSByTouch = /Mac/.test(platform) && navigator.maxTouchPoints > 1;
      const isIOS = iOSByUA || iOSAsDesktop || iOSByTouch;

      // Detect if running as installed PWA (standalone mode)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone === true;
      
      // iOS Safari without PWA install = no push support
      const iosSafariNoPWA = isIOS && !isStandalone;
      setIsIOSSafariNoPWA(iosSafariNoPWA);
      
      const supported = "Notification" in window && 
                       "serviceWorker" in navigator && 
                       "PushManager" in window &&
                       !iosSafariNoPWA; // iOS Safari without PWA doesn't truly support push
      setIsSupported(supported);
      
      if (supported) {
        setPermissionStatus(Notification.permission);
      }
    };
    
    checkSupport();
  }, []);

  // Get VAPID public key from backend
  const getVapidKey = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/reminders/vapid-key`);
      const data = await response.json();
      return data.publicKey;
    } catch (error) {
      console.error("[Nudge] Failed to get VAPID key:", error);
      return null;
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = useCallback(async () => {
    if (!isSupported) return null;
    
    try {
      const registration = await navigator.serviceWorker.ready;
      
      // Check for existing subscription
      let sub = await registration.pushManager.getSubscription();
      
      if (!sub) {
        // Get VAPID key and create new subscription
        const vapidKey = await getVapidKey();
        if (!vapidKey) return null;
        
        // Convert VAPID key to Uint8Array
        const urlBase64ToUint8Array = (base64String) => {
          const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
          const base64 = (base64String + padding)
            .replace(/-/g, "+")
            .replace(/_/g, "/");
          const rawData = window.atob(base64);
          const outputArray = new Uint8Array(rawData.length);
          for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
          }
          return outputArray;
        };
        
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey)
        });
      }
      
      setSubscription(sub);
      return sub.toJSON();
    } catch (error) {
      console.error("[Nudge] Push subscription failed:", error);
      return null;
    }
  }, [isSupported, getVapidKey]);

  // Enable nudge for a task
  const enableNudge = useCallback(async (taskId, taskName) => {
    if (!userId || !taskId) return { success: false };
    
    // Get push subscription
    const pushSubscription = await subscribeToPush();
    
    try {
      const response = await fetch(`${API_URL}/api/nudge/enable`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          taskId,
          taskName,
          enabled: true,
          pushSubscription
        })
      });
      
      return await response.json();
    } catch (error) {
      console.error("[Nudge] Enable failed:", error);
      return { success: false, error: error.message };
    }
  }, [userId, subscribeToPush]);

  // Disable nudge for a task
  const disableNudge = useCallback(async (taskId) => {
    if (!userId || !taskId) return { success: false };
    
    try {
      const response = await fetch(`${API_URL}/api/nudge/enable`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          taskId,
          taskName: "",
          enabled: false
        })
      });
      
      return await response.json();
    } catch (error) {
      console.error("[Nudge] Disable failed:", error);
      return { success: false, error: error.message };
    }
  }, [userId]);

  // Cancel nudge (when task is completed or unmarked important)
  const cancelNudge = useCallback(async (taskId) => {
    if (!taskId) return { success: false };
    
    try {
      const response = await fetch(`${API_URL}/api/nudge/cancel`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ taskId })
      });
      
      return await response.json();
    } catch (error) {
      console.error("[Nudge] Cancel failed:", error);
      return { success: false, error: error.message };
    }
  }, []);

  // Record activity (resets nudge timer)
  const recordActivity = useCallback(async (activityType = "interact") => {
    if (!userId) return;
    
    const now = Date.now();
    
    // Debounce activity pings (max once per 30 seconds)
    if (now - lastActivityRef.current < 30000) {
      return;
    }
    lastActivityRef.current = now;
    
    try {
      await fetch(`${API_URL}/api/nudge/activity`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, activityType })
      });
    } catch (error) {
      // Silently fail - activity tracking is not critical
      console.debug("[Nudge] Activity ping failed:", error);
    }
  }, [userId]);

  // Get nudge status for a task
  const getNudgeStatus = useCallback(async (taskId) => {
    if (!taskId) return { enabled: false };
    
    try {
      const response = await fetch(
        `${API_URL}/api/nudge/status/${taskId}`,
        { credentials: "include" }
      );
      return await response.json();
    } catch (error) {
      console.error("[Nudge] Status check failed:", error);
      return { enabled: false };
    }
  }, [userId]);

  // Check if permission is granted
  const hasPermission = permissionStatus === "granted";

  // Check if permission was denied
  const permissionDenied = permissionStatus === "denied";

  return {
    isSupported,
    isIOSSafariNoPWA, // iOS Safari user who hasn't installed PWA
    hasPermission,
    permissionDenied,
    permissionStatus,
    enableNudge,
    disableNudge,
    cancelNudge,
    recordActivity,
    getNudgeStatus,
    subscribeToPush
  };
};

export default useNudgeSystem;
